import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import noop from '@nullvoxpopuli/ember-composable-helpers/helpers/noop';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { ref } from 'ember-ref-bucket';
// @ts-ignore
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import BvChoice from 'volta/components/bv-choice';
import BvIcon from 'volta/components/bv-icon';
import classNames from 'volta/helpers/class-names';
import isPresent from 'volta/helpers/is-present';
import stopPropagation from 'volta/helpers/stop-propagation';
import trueStringIfPresent from 'volta/helpers/true-string-if-present';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import type { ContentValue } from '@glint/template';
interface BvCheckboxSignature {
    Args: {
        disabled?: boolean;
        required?: boolean;
        value?: boolean;
        testName?: string;
        inList?: boolean;
        name?: string;
        checked?: boolean | 'indeterminate';
        id?: string;
        error?: string;
        label?: ContentValue;
        helpText?: ContentValue;
        labelHidden?: boolean;
        ariaDescribedBy?: string;
        labelClass?: string;
        onChange?: (checked: boolean, event: MouseEvent) => void;
        onFocus?: () => void;
        onBlur?: () => void;
    };
    Element: HTMLElement;
}
export default class BvCheckboxComponent extends Component<BvCheckboxSignature> {
    @tracked
    checked?: boolean | 'indeterminate' = this.args.checked;
    /**
   * The input element.
   */ @ref('inputRef')
    inputRef?: HTMLInputElement;
    /**
   * The id of the checkbox.
   */ get id() {
        return this.args.id ?? `${guidFor(this)}BvCheckbox`;
    }
    /**
   * Whether the checkbox is in an indeterminate state.
   */ get isIndeterminate() {
        const { checked = false } = this;
        return checked === 'indeterminate';
    }
    /**
   * Whether the checkbox is checked.
   */ get isChecked() {
        const { checked } = this;
        return !this.isIndeterminate && Boolean(checked);
    }
    @action
    handleOnClick(e: MouseEvent) {
        const { onChange, disabled } = this.args;
        if ((disabled ?? !this.inputRef) || !onChange) {
            return;
        }
        // Checked and indeterminate state have been changed, but that's not DDAU!
        // Reset the change, send the action and wait for it to be changed manually
        const checked = this.inputRef.checked;
        this.checked = checked;
        const { isChecked, isIndeterminate } = this;
        this.inputRef.checked = isChecked;
        this.inputRef.indeterminate = isIndeterminate;
        onChange(checked, e);
        this.inputRef.focus();
    }
    @action
    handleCheckedChanged(_input: HTMLInputElement, [checked]: [boolean | 'indeterminate' | undefined]) {
        if (checked !== this.checked) {
            this.checked = checked;
        }
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    <BvChoice
      @id={{this.id}}
      @disabled={{@disabled}}
      @error={{@error}}
      @helpText={{@helpText}}
      @label={{@label}}
      @labelHidden={{@labelHidden}}
      @labelClass={{@labelClass}}
      ...attributes
      as |bvChoice|
    >
      <span class={{classNames 'bv-checkbox' (if @error 'bv-checkbox--error')}}>
        {{! template-lint-disable no-duplicate-id }}
        {{! template-lint-disable no-at-ember-render-modifiers }}
        <input
          aria-checked={{if this.isIndeterminate 'mixed' this.isChecked}}
          aria-describedby={{classNames @ariaDescribedBy bvChoice.helpTextId bvChoice.errorId}}
          aria-invalid={{isPresent @error}}
          checked={{this.isChecked}}
          class={{classNames
            'bv-checkbox__input'
            (if this.isIndeterminate 'bv-checkbox__input--indeterminate')
          }}
          disabled={{@disabled}}
          id={{this.id}}
          indeterminate={{trueStringIfPresent this.isIndeterminate}}
          name={{@name}}
          required={{@required}}
          role={{if @inList 'presentation' 'checkbox'}}
          type='checkbox'
          value={{@value}}
          data-test-bv-checkbox={{@testName}}
          {{on 'focus' (optional @onFocus)}}
          {{on 'blur' (optional @onBlur)}}
          {{on 'click' this.handleOnClick}}
          {{on 'change' (noop)}}
          {{createRef 'inputRef' bucket=this}}
          {{didUpdate this.handleCheckedChanged @checked}}
        />
        {{! template-lint-disable no-invalid-interactive }}
        <span
          class='bv-checkbox__backdrop'
          {{on 'click' (stopPropagation)}}
          {{on 'keyup' (stopPropagation)}}
        ></span>
        <span class='bv-checkbox__icon'>
          <BvIcon @glyph={{if this.isIndeterminate 'remove' 'check'}} @size='md' />
        </span>
      </span>
    </BvChoice>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        BvCheckbox: typeof BvCheckboxComponent;
        'bv-checkbox': typeof BvCheckboxComponent;
    }
}
